import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';
import { ChatCircleTextIcon } from '@phosphor-icons/react/dist/csr/ChatCircleText';
import { GavelIcon } from '@phosphor-icons/react/dist/csr/Gavel';

import { AnswerDialog } from './AnswerDialog';
import { DecideDialog } from './DecideDialog';
import { CATEGORY_LABEL_KEY, STATUS_LABEL_KEY } from './justificationLabels';
import { Button } from '../../design-system/primitives/Button/Button';
import { ApiError } from '../../lib/api/client';
import { justificationsApi, type JustificationRow } from '../../lib/api/justifications';
import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './JustificationDetail.module.css';

/**
 * The justification detail view (M-11, Task 4, spec §3) — the complaint,
 * the answer, and the decision with its note and its decider, ALL THREE
 * TOGETHER. That three-party record is the feature (spec §3's own words);
 * a detail view that shows only the current status is the legacy's own
 * dead end again — `admin-show-justifications.php` and its two siblings
 * never rendered the answer or the decision note anywhere a reader could
 * see the whole history in one place (spec §0).
 *
 * ## Actions come from the server's state, never re-derived here (Global Constraint)
 *
 * The Answer/Decide buttons are gated ENTIRELY on `record.can.answer`/
 * `record.can.decide` — booleans `JustificationResource` computes
 * server-side from `JustificationPolicy::answer()`/`::decide()` (plus, for
 * `decide`, the `status === 'answered'` state `JustificationTransition::
 * decide()` itself enforces with a `409`, J-7) for THIS caller against
 * THIS row. Nothing here re-implements `JustificationPolicy::answer()`'s
 * "must be the row's own `subject_id`" rule, or re-derives either button
 * from `permissions` + `status` the way an earlier revision of this file
 * did — see this module's own C-1 branch-review finding: that revision
 * showed "Answer" to every `justification.decide` holder browsing the
 * queue (an administrator or supervisor), and the server 403'd every
 * click, because holding the permission is not the same as being the row's
 * `subject_id`. Spec §3: "the answer form appears only when the server
 * says the caller may answer. Actions come from the server's state, never
 * re-derived in the client."
 *
 * ## The panel heading differs from the shell's `<h1>Conduct complaints</h1>`
 *
 * `justifications.detailTitle` names the record (the subject's name), not
 * the module — the identical defect Cases, Requests and Escalations have
 * each shipped, restated once more by the Global Constraints so a fourth
 * screen does not repeat it.
 */

export interface JustificationDetailProps {
  uuid: string;
  onBack: () => void;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function JustificationDetail({ uuid, onBack }: JustificationDetailProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [record, setRecord] = useState<JustificationRow | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [answerOpen, setAnswerOpen] = useState(false);
  const [decideOpen, setDecideOpen] = useState(false);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const found = await justificationsApi.get(uuid);
      setRecord(found);
      setStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [uuid]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleActionDone(updated: JustificationRow): void {
    setRecord(updated);
    setAnswerOpen(false);
    setDecideOpen(false);
  }

  if (status === 'loading') {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
          {t('justifications.back')}
        </Button>
        <p role="status">{t('list.loading')}</p>
      </div>
    );
  }

  if (status === 'error' || record === null) {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
          {t('justifications.back')}
        </Button>
        <div role="alert" className={styles['recovery']}>
          <p>{t('justifications.loadFailedDetail')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('justifications.retry')}
          </Button>
          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="justificationDetailRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      </div>
    );
  }

  const offerAnswer = record.can.answer;
  const offerDecide = record.can.decide;

  return (
    <div className={styles['page']}>
      <div className={styles['header']}>
        <div className={styles['identity']}>
          <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
            {t('justifications.back')}
          </Button>
          {/* `justifications.detailTitle`, NOT `justifications.title` — the
              shell already renders `<h1>Conduct complaints</h1>` from the nav
              entry; a panel heading repeating it is the defect this
              module's own docblock names. */}
          <h2 className={styles['title']}>
            {t('justifications.detailTitle', { subject: record.subject.full_name })}
          </h2>
        </div>

        {offerAnswer || offerDecide ? (
          <div className={styles['actions']}>
            {offerAnswer ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setAnswerOpen(true);
                }}
                icon={<ChatCircleTextIcon size={16} />}
              >
                {t('justifications.answerAction')}
              </Button>
            ) : null}
            {offerDecide ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setDecideOpen(true);
                }}
                icon={<GavelIcon size={16} />}
              >
                {t('justifications.decideAction')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <dl className={[styles['panel'], styles['primary'], styles['fields']].join(' ')}>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('justifications.fieldSubject')}</dt>
          <dd className={styles['fieldValue']}>{record.subject.full_name}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('justifications.fieldRaiser')}</dt>
          <dd className={styles['fieldValue']}>{record.raiser.full_name}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('justifications.fieldCategory')}</dt>
          <dd className={styles['fieldValue']}>{t(CATEGORY_LABEL_KEY[record.category])}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('table.status')}</dt>
          <dd className={styles['fieldValue']}>{t(STATUS_LABEL_KEY[record.status])}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('justifications.fieldOccurredAt')}</dt>
          <dd className={styles['fieldValue']}>
            {record.occurred_at === null
              ? t('table.notRecorded')
              : formatTimestamp(record.occurred_at, i18n.language)}
          </dd>
        </div>
        <div className={`${styles['field']} ${styles['fieldWide']}`}>
          <dt className={styles['fieldLabel']}>{t('justifications.fieldComplaint')}</dt>
          <dd className={styles['fieldValue']}>{record.complaint}</dd>
        </div>
      </dl>

      {/* THE THREE-PARTY RECORD (this file's own docblock) — the complaint
          above, the subject's own answer, and the decision with its note
          and its decider, all rendered together. `<dl>`, not `<div>` — the
          children are `<dt>`/`<dd>` pairs, and a term/description pair with
          no `<dl>` ancestor is `dlitem` (axe-core, impact: serious). The
          metadata block above already got this right. */}
      <section className={[styles['panel'], styles['section']].join(' ')}>
        <CardHead level="h3" icon={<ChatCircleTextIcon size={16} weight="duotone" />} title={t('justifications.answerSectionTitle')} />
        {record.justification === null ? (
          <p className={styles['sectionEmpty']}>{t('justifications.noAnswerYet')}</p>
        ) : (
          <dl className={styles['fields']}>
            <div className={`${styles['field']} ${styles['fieldWide']}`}>
              <dt className={styles['fieldLabel']}>{t('justifications.fieldAnswer')}</dt>
              <dd className={styles['fieldValue']}>{record.justification}</dd>
            </div>
            <div className={styles['field']}>
              <dt className={styles['fieldLabel']}>{t('justifications.fieldAnsweredAt')}</dt>
              <dd className={styles['fieldValue']}>
                {record.answered_at === null
                  ? t('table.notRecorded')
                  : formatTimestamp(record.answered_at, i18n.language)}
              </dd>
            </div>
          </dl>
        )}
      </section>

      <section className={[styles['panel'], styles['section']].join(' ')}>
        <CardHead level="h3" icon={<GavelIcon size={16} weight="duotone" />} title={t('justifications.decisionSectionTitle')} />
        {record.decider === null ? (
          <p className={styles['sectionEmpty']}>{t('justifications.noDecisionYet')}</p>
        ) : (
          <dl className={styles['fields']}>
            <div className={styles['field']}>
              <dt className={styles['fieldLabel']}>{t('justifications.fieldDecider')}</dt>
              <dd className={styles['fieldValue']}>{record.decider.full_name}</dd>
            </div>
            <div className={styles['field']}>
              <dt className={styles['fieldLabel']}>{t('justifications.fieldDecidedAt')}</dt>
              <dd className={styles['fieldValue']}>
                {record.decided_at === null
                  ? t('table.notRecorded')
                  : formatTimestamp(record.decided_at, i18n.language)}
              </dd>
            </div>
            {record.decision_note === null ? null : (
              <div className={`${styles['field']} ${styles['fieldWide']}`}>
                <dt className={styles['fieldLabel']}>{t('justifications.fieldDecisionNote')}</dt>
                <dd className={styles['fieldValue']}>{record.decision_note}</dd>
              </div>
            )}
          </dl>
        )}
      </section>

      <AnswerDialog
        open={answerOpen}
        justificationUuid={record.uuid}
        subjectName={record.subject.full_name}
        onClose={() => {
          setAnswerOpen(false);
        }}
        onDone={handleActionDone}
      />

      <DecideDialog
        open={decideOpen}
        justificationUuid={record.uuid}
        subjectName={record.subject.full_name}
        onClose={() => {
          setDecideOpen(false);
        }}
        onDone={handleActionDone}
      />
    </div>
  );
}
